import dayjs, { Dayjs } from 'dayjs';
import { Options } from '../types';
import { getDaysInMonth } from '../utils';

interface CreateStore {
  setInitialState: (newState: string) => void;
  setState: (newDate: Partial<DateStore>) => void;
  getSnapshot: () => DateStore;
  subscribe: (onStoreChange: () => void) => () => void;
  resetDateStore: () => void;
  today: Dayjs;
}

export interface DateStore {
  year: number | undefined;
  month: number | undefined;
  day: number | undefined;
  today: Dayjs;
}

const createStore = (): CreateStore => {
  const today = dayjs();

  const state: { current: DateStore } = {
    current: {
      year: undefined,
      month: undefined,
      day: undefined,
      today
    }
  };

  const listeners = new Set<() => void>();

  const setInitialState = <T extends Options>(initialDate: T) => {
    const date = dayjs(initialDate);

    if (date.format('YYYY-MM-DD') === 'Invalid Date') {
      return;
    }

    const year = date.year();
    const month = date.month();
    const day = date.date();

    state.current = { year, month, day, today };
    listeners.forEach((listener) => listener());
  };

  const setState = (newState: Partial<DateStore>) => {
    const previousState = state.current;
    const updatedState = { ...previousState, ...newState };
    const year = updatedState.year || previousState.year || today.year();
    const month = updatedState.month ?? 0;
    const maxDaysInMonth = getDaysInMonth(year, month);

    if (updatedState.day && updatedState.day > maxDaysInMonth) {
      updatedState.day = undefined;
    }

    const updatedToday = dayjs()
      .year(today.year())
      .month(updatedState.month || today.month())
      .date(updatedState.day || today.date());

    state.current = { ...updatedState, today: updatedToday };
    listeners.forEach((listener) => listener());
  };

  const getSnapshot = (): DateStore => state.current;

  const resetDateStore = () => {
    setState({ year: undefined, month: undefined, day: undefined });
  };

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return { getSnapshot, setInitialState, subscribe, setState, resetDateStore, today };
};

export const dateStore = createStore();
